import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import {
	DEFAULT_PROPOSAL_THEME,
	DEFAULT_PROPOSAL_VARIABLES,
	INITIAL_PROPOSAL_FORM_DATA,
} from "../constants/proposalDefaults";
import { parseJsonField } from "../utils/proposalHelpers";
import { useVisualBuilder } from "./useVisualBuilder";

/**
 * Hook customizado para gerenciar a complexa lógica de criação/edição de propostas
 */
export const useProposalEditor = (editId, showNotification) => {
	const navigate = useNavigate();

	// Listas de referência
	const [companies, setCompanies] = useState([]);
	const [clients, setClients] = useState([]);
	const [plans, setPlans] = useState([]);
	const [modules, setModules] = useState([]);
	const [templatesByCategory, setTemplatesByCategory] = useState({});
	const [selectedTemplates, setSelectedTemplates] = useState({});

	// Estados de controle
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [errors, setErrors] = useState({});
	const [step, setStep] = useState(1);
	const [createdProposal, setCreatedProposal] = useState(null);
	const [isDirty, setIsDirty] = useState(false);
	const skipDirtyRef = useRef(editId ? 2 : 1);

	// Dados do formulário
	const [formData, setFormData] = useState({ ...INITIAL_PROPOSAL_FORM_DATA });

	// Visual Builder logic
	const visualBuilder = useVisualBuilder([], []);
	const { sections, selectedItems, setAllSections, setAllItems, totalValue } =
		visualBuilder;

	/**
	 * Busca todos os dados necessários (master data)
	 */
	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const [companiesRes, clientsRes, plansRes, modulesRes, templatesRes] =
				await Promise.all([
					client.get("/companies/all"),
					client.get("/clients/all"),
					client.get("/plans", { params: { limit: 500 } }),
					client.get("/modules", { params: { limit: 500 } }),
					client.get("/templates/by-category"),
				]);

			setCompanies(companiesRes.data);
			setClients(clientsRes.data);
			setPlans(plansRes.data.data || plansRes.data);
			setModules(modulesRes.data.data || modulesRes.data);
			setTemplatesByCategory(templatesRes.data || {});

			// Se for edição, carrega a proposta
			if (editId) {
				const proposalRes = await client.get(`/proposals/${editId}`);
				const { proposal, items } = proposalRes.data;

				const tmplData = parseJsonField(proposal.template_data) || {};
				const acceptance = parseJsonField(proposal.acceptance_config);

				setFormData({
					client_id: proposal.client_id?.toString() || "",
					company_id: proposal.company_id?.toString() || "",
					title: proposal.title || "",
					description: proposal.description || "",
					notes: proposal.notes || "",
					validity_days: proposal.validity_days || 30,
					template_id: proposal.template_id?.toString() || "",
					theme: tmplData.theme || { ...DEFAULT_PROPOSAL_THEME },
					variables: tmplData.variables || { ...DEFAULT_PROPOSAL_VARIABLES },
					acceptance_config: acceptance || {
						...INITIAL_PROPOSAL_FORM_DATA.acceptance_config,
					},
					code_type: tmplData.code_type || "react",
				});

				setAllSections(parseJsonField(proposal.sections) || []);
				if (items && Array.isArray(items)) setAllItems(items);
			}
		} catch (error) {
			console.error("Error fetching data:", error);
			if (showNotification) showNotification("Erro ao carregar dados", "error");
		} finally {
			setLoading(false);
		}
	}, [editId, showNotification, setAllSections, setAllItems]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Track dirty state
	useEffect(() => {
		if (skipDirtyRef.current > 0) {
			skipDirtyRef.current--;
			return;
		}
		setIsDirty(true);
	}, []);

	/**
	 * Lógica de aplicação de templates por categoria
	 */
	const handleCategoryTemplateSelect = useCallback(
		(category, templateId) => {
			if (!templateId) {
				setSelectedTemplates((prev) => {
					const next = { ...prev };
					delete next[category];
					return next;
				});
				if (category === "full") {
					setFormData((prev) => ({
						...prev,
						template_id: null,
					}));
				}
				return;
			}

			const findTmpl = (id) => {
				for (const cat of Object.values(templatesByCategory)) {
					const found = cat.find((t) => t.id === parseInt(id, 10));
					if (found) return found;
				}
				return null;
			};

			const tmpl = findTmpl(templateId);
			if (!tmpl) return;

			setSelectedTemplates((prev) => {
				const next =
					category === "full" ? { full: tmpl } : { ...prev, [category]: tmpl };
				if (category === "full") delete next.products;
				else delete next.full;
				return next;
			});

			if (category === "full") {
				setFormData((prev) => ({
					...prev,
					template_id: tmpl.id,
				}));
			}

			// Aplica seções e dados do template
			const s = parseJsonField(tmpl.sections);
			const td = parseJsonField(tmpl.template_data) || {};
			if (Array.isArray(s)) setAllSections((prev) => [...prev, ...s]);
			setFormData((prev) => ({
				...prev,
				code_type: td.code_type || "react",
				theme: { ...prev.theme, ...(td.theme || {}) },
				variables: { ...prev.variables, ...(td.variables || {}) },
			}));
			if (showNotification) showNotification("Template aplicado!");
		},
		[templatesByCategory, setAllSections, showNotification],
	);

	const hasCodeTemplate = useMemo(() => {
		const selectedTemplateId = formData.template_id;
		if (!selectedTemplateId) return false;
		for (const cat of Object.values(templatesByCategory)) {
			const found = cat.find(
				(t) => String(t.id) === String(selectedTemplateId),
			);
			if (found?.template_code) return true;
		}
		return false;
	}, [formData.template_id, templatesByCategory]);

	/**
	 * Validação por etapa
	 */
	const validateStep = useCallback(
		(s) => {
			const newErrors = {};
			if (s === 1) {
				if (!formData.client_id) newErrors.client_id = "Cliente obrigatório";
				if (!formData.company_id) newErrors.company_id = "Empresa obrigatória";
			}
			if (s === 3) {
				// Se o template selecionado tiver código, itens não são obrigatórios!
				const selectedTemplateId = formData.template_id;
				let hasCode = false;
				if (selectedTemplateId) {
					for (const cat of Object.values(templatesByCategory)) {
						const found = cat.find(
							(t) => String(t.id) === String(selectedTemplateId),
						);
						if (found?.template_code) {
							hasCode = true;
							break;
						}
					}
				}
				if (!hasCode && selectedItems.length === 0) {
					newErrors.items = "Adicione pelo menos um item";
				}
			}
			if (s === 5) {
				if (
					formData.acceptance_config?.require_passcode &&
					!formData.acceptance_config?.passcode?.trim()
				) {
					newErrors.passcode = "Senha (Passcode) é obrigatória quando ativada";
				}
			}
			setErrors(newErrors);
			return Object.keys(newErrors).length === 0;
		},
		[formData, selectedItems, templatesByCategory],
	);

	/**
	 * Finalização e salvamento
	 */
	const handleSubmit = useCallback(async () => {
		if (!validateStep(1)) {
			setStep(1);
			return;
		}
		if (!hasCodeTemplate && !validateStep(3)) {
			setStep(3);
			return;
		}
		if (!validateStep(5)) {
			setStep(5);
			return;
		}
		try {
			setSaving(true);
			const payload = {
				...formData,
				items: selectedItems,
				sections: sections.filter(
					(s) => s.title || s.content || s.type === "company_intro",
				),
				template_ids: Object.values(selectedTemplates)
					.filter(Boolean)
					.map((t) => t.id),
				template_data: {
					theme: formData.theme,
					code_type: formData.code_type || "react",
					variables: {
						...formData.variables,
						total_value: `R$ ${totalValue.toFixed(2)}`,
					},
				},
			};

			if (editId) {
				await client.put(`/proposals/${editId}`, payload);
				if (showNotification) showNotification("Proposta atualizada!");
				navigate(`/admin/proposals/${editId}`);
			} else {
				const res = await client.post("/proposals", payload);
				setCreatedProposal(res.data);
			}
			setIsDirty(false);
		} catch (error) {
			console.error("Error saving proposal:", error);
			if (showNotification)
				showNotification("Erro ao salvar proposta", "error");
		} finally {
			setSaving(false);
		}
	}, [
		validateStep,
		formData,
		selectedItems,
		sections,
		selectedTemplates,
		totalValue,
		editId,
		showNotification,
		navigate,
		hasCodeTemplate,
	]);

	const handleNext = useCallback(() => {
		if (validateStep(step)) {
			setStep((prev) => {
				if (hasCodeTemplate && prev === 1) return 5;
				return Math.min(prev + 1, 5);
			});
		}
	}, [step, validateStep, hasCodeTemplate]);

	const handleBack = useCallback(() => {
		setStep((prev) => {
			if (hasCodeTemplate && prev === 5) return 1;
			return Math.max(prev - 1, 1);
		});
	}, [hasCodeTemplate]);

	return {
		companies,
		clients,
		plans,
		modules,
		templatesByCategory,
		selectedTemplates,
		loading,
		saving,
		errors,
		setErrors,
		step,
		setStep,
		createdProposal,
		formData,
		setFormData,
		isDirty,
		setIsDirty,
		visualBuilder,
		handleCategoryTemplateSelect,
		handleSubmit,
		handleNext,
		handleBack,
		isEditing: !!editId,
		hasCodeTemplate,
	};
};
